import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Select, Skeleton, Tag, message } from 'antd';
import { employeesApi } from '../../utils/api';

const GROUPS = [
  { key: 'crm', title: 'CRM: معاملات و لیدها' },
  { key: 'follow_ups', title: 'پیگیری‌ها' },
  { key: 'work', title: 'کار و پروژه' },
  { key: 'other', title: 'تیکت، تماس و گردش‌کار' },
];

const CATEGORY_LABELS = {
  deals: { label: 'معاملات جاری (مالک معامله)', hint: 'معاملاتی که هنوز برنده یا بازنده نشده‌اند' },
  lead_contacts: { label: 'لیدها و مخاطبین باز', hint: 'مالک لید: جدید، در حال پیگیری یا تأییدشده' },
  lead_forms: { label: 'فرم‌های لید', hint: 'فرم‌هایی که مالک آن این کارمند است' },
  follow_ups: { label: 'پیگیری‌های انجام‌نشده', hint: 'پیگیری‌های باز تخصیص‌یافته به او' },
  tasks: { label: 'تسک‌های باز', hint: 'تسک‌های انجام‌نشده‌ای که مسئول آن است' },
  sub_tasks: { label: 'زیرتسک‌های باز', hint: '' },
  projects: { label: 'پروژه‌های باز', hint: 'مدیر پروژه یا عضو پروژه‌های در جریان' },
  tickets: { label: 'تیکت‌های باز', hint: 'تیکت‌های باز یا در انتظار که مسئول آن است' },
  call_center_extension: { label: 'داخلی مرکز تماس', hint: 'فقط به نفر اول انتخاب‌شده داده می‌شود' },
  workflow_approvals: { label: 'تأییدکنندهٔ گردش‌کار', hint: 'مراحل گردش‌کار و درخواست‌های منتظر تأیید او' },
};

const extractList = (res) => {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

/**
 * حذف کارمند در دو مرحله: ۱) واگذاری موارد باز به یک یا چند نفر ۲) حذف (نرم).
 * تا همهٔ موارد باز واگذار نشده باشد، دکمهٔ حذف غیرفعال است.
 */
const EmployeeHandoverModal = ({ employee, open, onClose, onDeleted }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [candidates, setCandidates] = useState([]);
  // { [categoryKey]: number[] }
  const [selection, setSelection] = useState({});
  const [bulkTargets, setBulkTargets] = useState([]);

  const employeeId = employee?.id;

  const loadSummary = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    try {
      const res = await employeesApi.getHandoverSummary(employeeId);
      setSummary(res?.data ?? res);
    } catch (error) {
      console.error('Error loading handover summary:', error);
      message.error('خطا در دریافت موارد باز کارمند');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (!open || !employeeId) return undefined;
    setSelection({});
    setBulkTargets([]);
    setSummary(null);
    loadSummary();

    let cancelled = false;
    (async () => {
      try {
        const res = await employeesApi.getAll({ status: 'active' });
        if (cancelled) return;
        setCandidates(
          extractList(res)
            .filter((e) => Number(e.id) !== Number(employeeId))
            .map((e) => ({ value: Number(e.id), label: e.name || e.email || 'کاربر نامشخص' }))
            .sort((a, b) => a.label.localeCompare(b.label, 'fa')),
        );
      } catch (error) {
        console.error('Error loading handover candidates:', error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, employeeId, loadSummary]);

  const openCategories = useMemo(
    () => (summary?.categories || []).filter((c) => c.count > 0),
    [summary],
  );

  const pending = openCategories.filter((c) => (selection[c.key] || []).length > 0);
  const canDelete = summary?.can_delete === true;

  const applyToAll = (ids) => {
    setBulkTargets(ids);
    setSelection(Object.fromEntries(openCategories.map((c) => [c.key, ids])));
  };

  const handleHandover = async () => {
    setSubmitting(true);
    try {
      const assignments = pending.map((c) => ({ category: c.key, user_ids: selection[c.key] }));
      const res = await employeesApi.handover(employeeId, assignments);
      setSummary(res?.data ?? res);
      setSelection({});
      setBulkTargets([]);
      message.success('موارد انتخاب‌شده واگذار شد');
    } catch (error) {
      console.error('Error handing over employee items:', error);
      message.error(error?.message || 'خطا در واگذاری موارد');
      loadSummary();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await employeesApi.delete(employeeId);
      message.success('کارمند حذف شد (اطلاعات و تاریخچهٔ او نگه‌داری می‌شود)');
      onDeleted?.(employeeId);
      onClose?.();
    } catch (error) {
      console.error('Error deleting employee:', error);
      message.error('حذف انجام نشد؛ ممکن است موارد بازی باقی مانده باشد');
      loadSummary();
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: <span style={{ fontFamily: 'CustomFont' }}>حذف {employee?.name}</span>,
      content: (
        <span style={{ fontFamily: 'CustomFont' }}>
          کارمند غیرفعال و از لیست‌ها حذف می‌شود و دیگر نمی‌تواند وارد شود. سوابق و تاریخچهٔ او نگه‌داری می‌شود.
        </span>
      ),
      okText: 'حذف کارمند',
      cancelText: 'لغو',
      okButtonProps: { danger: true },
      onOk: handleDelete,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={760}
      destroyOnClose
      title={<span style={{ fontFamily: 'CustomFont' }}>حذف و واگذاری موارد: {employee?.name}</span>}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2" style={{ fontFamily: 'CustomFont' }}>
          <Button onClick={onClose}>بستن</Button>
          <Button
            onClick={handleHandover}
            loading={submitting}
            disabled={pending.length === 0}
            style={{ fontFamily: 'CustomFont' }}
          >
            واگذاری موارد انتخاب‌شده
          </Button>
          <Button
            danger
            type="primary"
            onClick={confirmDelete}
            disabled={!canDelete || submitting}
            style={{ fontFamily: 'CustomFont' }}
          >
            حذف کارمند
          </Button>
        </div>
      }
    >
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        {loading && !summary ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : !summary ? null : canDelete ? (
          <Alert
            type="success"
            showIcon
            message="همهٔ موارد واگذار شده است"
            description="این کارمند مورد بازی ندارد و می‌توانید حذفش کنید. حذف از نوع «نرم» است: سوابق و تاریخچه می‌ماند."
          />
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              className="mb-4"
              message="پیش از حذف باید موارد باز او به همکاران دیگر واگذار شود"
              description="برای هر دسته یک یا چند نفر را انتخاب کنید؛ اگر چند نفر انتخاب شود، موارد به‌صورت مساوی بینشان تقسیم می‌شود. موارد بسته و تاریخچه دست‌نخورده می‌ماند."
            />

            <div className="mb-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="mb-1 text-sm font-medium">واگذاری همهٔ موارد به یک یا چند نفر</div>
              <Select
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="انتخاب همکاران"
                value={bulkTargets}
                options={candidates}
                onChange={applyToAll}
                style={{ width: '100%', fontFamily: 'CustomFont' }}
              />
            </div>

            {GROUPS.map((group) => {
              const rows = openCategories.filter((c) => c.group === group.key);
              if (rows.length === 0) return null;
              return (
                <div key={group.key} className="mb-4">
                  <div className="mb-2 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    {group.title}
                  </div>
                  {rows.map((c) => {
                    const meta = CATEGORY_LABELS[c.key] || { label: c.key, hint: '' };
                    return (
                      <div
                        key={c.key}
                        className="mb-2 grid grid-cols-1 items-center gap-2 rounded-lg border border-slate-100 dark:border-slate-700 p-2 md:grid-cols-[1fr_1.4fr]"
                      >
                        <div>
                          <div className="text-sm">
                            {meta.label} <Tag color="orange">{c.count.toLocaleString('fa-IR')}</Tag>
                          </div>
                          {meta.hint ? <div className="text-[11px] text-slate-400">{meta.hint}</div> : null}
                        </div>
                        <Select
                          mode="multiple"
                          showSearch
                          optionFilterProp="label"
                          placeholder="انتخاب همکاران"
                          value={selection[c.key] || []}
                          options={candidates}
                          onChange={(ids) => setSelection((prev) => ({ ...prev, [c.key]: ids }))}
                          maxTagCount="responsive"
                          style={{ width: '100%', fontFamily: 'CustomFont' }}
                        />
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </div>
    </Modal>
  );
};

export default EmployeeHandoverModal;
